// src/components/Settings/Settings.js - Modificado para restringir edição para colaboradores
import React, { useState, useEffect } from "react";
import { auth, db } from "../../firebase/config";
import { doc, getDoc, updateDoc, setDoc } from "firebase/firestore";
import { Link } from "react-router-dom";
import MainLayout from "../Layout/MainLayout";
import AccountForm from "./AccountForm";
import CategoryCostTypes from "./CategoryCostTypes";
import { useUserContext } from "../../contexts/UserContext";
import "./Settings.css";

const Settings = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("account");
  const [categoriesData, setCategoriesData] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  
  // Obter o tipo de conta ativa, informações de subscription e status de colaborador do contexto
  const { 
    activeAccountType, 
    subscription, 
    hasAccess,
    businessProfiles,
    createProfile,
    isCollaborator,
    collaboratorWorkspaces
  } = useUserContext();

  // Verificar se o usuário tem plano business_pro
  const hasPro = hasAccess('business_pro');

  // Carregar dados do usuário e categorias
  useEffect(() => {
    const loadUserData = async () => {
      try {
        setLoading(true);
        
        if (auth.currentUser) {
          const currentUser = auth.currentUser;
          setUser(currentUser);
          
          // Carregar categorias do usuário
          await loadUserCategories(currentUser.uid);
        }
      } catch (error) {
        console.error("Erro ao carregar dados do usuário:", error);
        setError("Não foi possível carregar seus dados. Tente novamente.");
      } finally {
        setLoading(false);
      }
    };
    
    loadUserData();
  }, [activeAccountType]);

  // Carregar categorias do usuário com base no tipo de conta ativa
  const loadUserCategories = async (userId) => {
    try {
      // MODIFICADO: Verificar se é um colaborador para carregar categorias do dono
      let ownerUserId = userId;
      
      if (isCollaborator && activeAccountType === 'business') {
        // Encontrar o workspace atual nos collaboratorWorkspaces
        const currentWorkspace = collaboratorWorkspaces.find(
          w => w.id === localStorage.getItem('active_business_profile_id')
        );
        
        if (currentWorkspace && currentWorkspace.ownerUid) {
          // Usar o ID do dono do workspace
          ownerUserId = currentWorkspace.ownerUid;
          console.log(`Carregando categorias do dono (${ownerUserId}) como colaborador`);
        }
      }
      
      // Carregar categorias do usuário ou do dono (se for colaborador)
      const userCategoriesDoc = await getDoc(doc(db, "userCategories", ownerUserId));
      
      if (userCategoriesDoc.exists()) {
        const allData = userCategoriesDoc.data();
        
        // Determinar quais categorias carregar baseado no tipo de conta ativa
        if (activeAccountType === 'personal') {
          // Para conta pessoal
          setCategoriesData({
            categories: allData.personalCategories || {},
            categoryOrder: allData.personalCategoryOrder || {},
            businessModel: allData.personalModel || "pessoal"
          });
        } else {
          // Para conta empresarial (compatibilidade com dados antigos)
          setCategoriesData({
            categories: allData.businessCategories || allData.categories || {},
            categoryOrder: allData.businessCategoryOrder || allData.categoryOrder || {},
            businessModel: allData.businessModel || "restaurante"
          });
        }
      }
    } catch (error) {
      console.error("Erro ao carregar categorias do usuário:", error);
      setError("Não foi possível carregar suas categorias.");
    }
  };

  // Organizar as categorias em grupos
  const organizeCategories = () => {
    if (!categoriesData || !categoriesData.categories) {
      return {
        groupedCategories: {},
        totalCategories: 0
      };
    }

    const categories = categoriesData.categories;
    const categoryOrder = categoriesData.categoryOrder || {};
    
    const selectedKeys = Object.keys(categories).filter(key => categories[key] === true);
    const groupedCategories = {};

    selectedKeys.forEach(key => {
      const parts = key.split('.');
      const groupName = parts[0];
      
      if (!groupedCategories[groupName]) {
        const order = categoryOrder[groupName] || 999;
        groupedCategories[groupName] = {
          normalCategories: [],
          subGroups: {},
          order: order
        };
      }
      
      if (parts.length === 2) {
        groupedCategories[groupName].normalCategories.push(parts[1]);
      } else if (parts.length === 3) {
        const subGroupName = parts[1];
        const categoryName = parts[2];
        
        if (!groupedCategories[groupName].subGroups[subGroupName]) {
          groupedCategories[groupName].subGroups[subGroupName] = [];
        }
        groupedCategories[groupName].subGroups[subGroupName].push(categoryName);
      }
    });

    return {
      groupedCategories,
      totalCategories: selectedKeys.length
    };
  };

  // Componente para a aba de Categorias
  const CategoriesTab = () => {
    const { groupedCategories, totalCategories } = organizeCategories();
    
    return (
      <div className="settings-tab-content">
        <div className="categories-header">
          <h3>
            {activeAccountType === 'personal' 
              ? 'Suas Categorias Financeiras Pessoais' 
              : 'Suas Categorias Financeiras Empresariais'}
          </h3>
          
          {/* MODIFICADO: Esconder botão "Editar Categorias" para colaboradores */}
          {!isCollaborator ? (
            <Link to="/select-categories" className="edit-categories-button">
              Editar Categorias
            </Link>
          ) : (
            <div className="collaborator-notice">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '16px', height: '16px', marginRight: '8px' }}>
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="8" x2="12" y2="12"></line>
                <line x1="12" y1="16" x2="12.01" y2="16"></line>
              </svg>
              <span>Apenas visualização (Modo Colaborador)</span>
            </div>
          )}
        </div>
        
        {/* MODIFICADO: Adicionar aviso para colaboradores */}
        {isCollaborator && (
          <div className="collaborator-info" style={{
            backgroundColor: "#f8f0e3",
            borderRadius: "6px",
            padding: "12px 16px",
            marginBottom: "20px",
            borderLeft: "4px solid #e67e22",
            fontSize: "14px",
            color: "#7d5a1c"
          }}>
            <p><strong>Modo Visualização:</strong> Como colaborador, você pode visualizar as categorias financeiras, mas não pode modificá-las. Apenas o proprietário da conta pode gerenciar as categorias.</p>
          </div>
        )}
        
        <p className="categories-count">
          Você selecionou {totalCategories} categorias.
        </p>
        
        <div className="categories-grid">
          {Object.entries(groupedCategories)
            .sort(([, dataA], [, dataB]) => dataA.order - dataB.order)
            .map(([groupName, groupData], index) => (
              <div key={index} className="category-group">
                <div className="category-group-header">{groupName}</div>
                
                {groupData.normalCategories.length > 0 && (
                  <ul className="category-list">
                    {groupData.normalCategories.map((category, catIndex) => (
                      <li key={catIndex} className="category-item">{category}</li>
                    ))}
                  </ul>
                )}
                
                {Object.entries(groupData.subGroups).map(([subGroupName, categories], subIndex) => (
                  <div key={subIndex} className="category-subgroup">
                    <h4 className="subgroup-title">{subGroupName}</h4>
                    <ul className="category-list">
                      {categories.map((category, catIndex) => (
                        <li key={catIndex} className="category-item">{category}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            ))}
        </div>
      </div>
    );
  };

  // Componente para a aba de Funcionalidades - ATUALIZADO
  const FeaturesTab = () => {
    const [showAportes, setShowAportes] = useState(() => {
      const saved = localStorage.getItem('showAportesSetting');
      return saved === 'true';
    });

    const [hideRevenueDeductions, setHideRevenueDeductions] = useState(() => {
      const saved = localStorage.getItem("hideRevenueDeductionsDashboard");
      return saved === "true"; 
    });

    // Estado para configuração de categorização automática de bancos
    const [automaticBankCategorization, setAutomaticBankCategorization] = useState(() => {
      const saved = localStorage.getItem("automaticBankCategorizationOFX");
      return saved !== "false"; // Por padrão, está ativado
    });

    // State para opção de conta pessoal
    const [hasPersonalAccount, setHasPersonalAccount] = useState(false);
    const [creatingPersonalAccount, setCreatingPersonalAccount] = useState(false);

    // Obter o contexto do usuário para acessar a função de atualização
    const { activeAccountType, updateAutomaticBankCategorization } = useUserContext();

    // Verificar se a conta pessoal já existe ao carregar - ATUALIZADO
    useEffect(() => {
      const checkPersonalAccount = async () => {
        if (user && user.uid && hasPro) {
          try {
            // Verificar usando a nova estrutura (perfil com profileType = 'personal')
            const hasPersonalProfile = businessProfiles.some(profile => profile.profileType === 'personal');
            setHasPersonalAccount(hasPersonalProfile);
          } catch (err) {
            console.error("Erro ao verificar conta pessoal:", err);
          }
        }
      };
      
      checkPersonalAccount();
    }, [user, hasPro, businessProfiles]);

    // MODIFICADO: Verificar se é colaborador e desabilitar funcionalidades se for
    if (isCollaborator) {
      return (
        <div className="settings-tab-content">
          <h3>Funcionalidades</h3>
          
          <div className="collaborator-info" style={{
            backgroundColor: "#f8f0e3",
            borderRadius: "6px",
            padding: "12px 16px",
            marginBottom: "20px",
            borderLeft: "4px solid #e67e22",
            fontSize: "14px",
            color: "#7d5a1c"
          }}>
            <p><strong>Modo Visualização:</strong> Como colaborador, você não pode alterar as configurações de funcionalidades. Estas configurações são gerenciadas pelo proprietário da conta.</p>
          </div>
          
          <div className="features-container">
            {/* Mostrar as configurações atuais em modo somente leitura */}
            <div className="feature-option">
              <div className="feature-label" style={{ opacity: 0.7 }}>
                <input 
                  type="checkbox" 
                  checked={showAportes}
                  disabled={true}
                />
                <span className="feature-text">
                  Mostrar aportes e despesas de sócios ao resultado final no Dashboard
                </span>
              </div>
              <p className="feature-description">
                Quando ativado, exibe a opção para incluir aportes e despesas de sócios nos cálculos de resultado final no Dashboard. Isso permite uma visão mais completa do seu fluxo financeiro.
              </p>
            </div>
            
            <div className="feature-option">
              <div className="feature-label" style={{ opacity: 0.7 }}>
                <input 
                  type="checkbox" 
                  checked={automaticBankCategorization}
                  disabled={true}
                />
                <span className="feature-text">
                  Categorização automática para bancos Sicoob/Stone (arquivos OFX)
                </span>
              </div>
              <p className="feature-description">
                Quando ativado, aplica regras de categorização específicas para receitas de bancos Sicoob e Stone importadas via OFX.
                Quando desativado, todas as receitas são categorizadas como "RECEITA.Receitas" independente da origem.
              </p>
            </div>
            
            <div className="feature-option">
              <div className="feature-label" style={{ opacity: 0.7 }}>
                <input
                  type="checkbox"
                  checked={hideRevenueDeductions}
                  disabled={true}
                />
                <span className="feature-text">
                  Ocultar "Deduções da Receita" na tabela de Evolução dos Resultados no Dashboard
                </span>
              </div>
              <p className="feature-description">
                Quando marcado, a coluna "Deduções da Receita" será omitida da tabela de resumo na seção "Evolução dos Resultados" do Dashboard para simplificar a visualização dos demais dados. Por padrão, esta coluna é exibida.
              </p>
            </div>
          </div>
        </div>
      );
    }

    // Versão original para usuários não-colaboradores
    const handleShowAportesChange = async () => {
      const newValue = !showAportes;
      setShowAportes(newValue);
      localStorage.setItem('showAportesSetting', newValue.toString());
      
      if (user && user.uid) {
        try {
          const userPrefsRef = doc(db, "userPreferences", user.uid);
          await updateDoc(userPrefsRef, {
            showAportesOption: newValue,
            updatedAt: new Date()
          }).catch(async (error) => {
            if (error.code === 'not-found') {
              await setDoc(userPrefsRef, {
                showAportesOption: newValue,
                createdAt: new Date(),
                updatedAt: new Date()
              });
            } else {
              throw error;
            }
          });
          setSuccess('Configuração de aportes salva com sucesso!');
        } catch (err) {
          console.error("Erro ao salvar preferência de aportes:", err);
          setError('Erro ao salvar configuração de aportes.');
        } finally {
            setTimeout(() => {
                setSuccess('');
                setError('');
            }, 3000);
        }
      } else {
        setSuccess('Configuração salva localmente!');
         setTimeout(() => setSuccess(''), 3000);
      }
    };

    const handleHideRevenueDeductionsChange = async () => {
      const newValue = !hideRevenueDeductions;
      setHideRevenueDeductions(newValue);
      localStorage.setItem("hideRevenueDeductionsDashboard", newValue.toString());

      if (user && user.uid) {
        try {
          const userPrefsRef = doc(db, "userPreferences", user.uid);
          await updateDoc(userPrefsRef, {
            hideRevenueDeductionsDashboard: newValue,
            updatedAt: new Date(),
          }).catch(async (err) => {
            if (err.code === "not-found") {
              await setDoc(userPrefsRef, {
                hideRevenueDeductionsDashboard: newValue,
                createdAt: new Date(),
                updatedAt: new Date(),
              });
            } else {
              throw err;
            }
          });
          setSuccess("Configuração de 'Deduções da Receita' salva!");
        } catch (err) {
          console.error("Erro ao salvar preferência de Deduções da Receita:", err);
          setError("Erro ao salvar configuração de 'Deduções da Receita'.");
        } finally {
          setTimeout(() => {
            setSuccess("");
            setError("");
          }, 3000);
        }
      } else {
          setSuccess('Configuração salva localmente!');
          setTimeout(() => setSuccess(''), 3000);
      }
    };

    // Function para lidar com a mudança na categorização automática
    const handleAutomaticBankCategorizationChange = async () => {
      const newValue = !automaticBankCategorization;
      setAutomaticBankCategorization(newValue);
      
      if (user && user.uid) {
        try {
          // Usar a função do contexto para atualizar o valor
          await updateAutomaticBankCategorization(newValue);
          setSuccess('Configuração de categorização automática salva com sucesso!');
        } catch (err) {
          console.error("Erro ao salvar preferência de categorização automática:", err);
          setError('Erro ao salvar configuração de categorização automática.');
        } finally {
          setTimeout(() => {
            setSuccess('');
            setError('');
          }, 3000);
        }
      } else {
        localStorage.setItem("automaticBankCategorizationOFX", newValue.toString());
        setSuccess('Configuração salva localmente!');
        setTimeout(() => setSuccess(''), 3000);
      }
    };

    // CORREÇÃO: Função atualizada para criar conta pessoal SEM window.location.reload()
    const handleCreatePersonalAccount = async () => {
      if (!user || !user.uid) {
        setError("Você precisa estar autenticado para criar uma conta pessoal.");
        return;
      }

      setCreatingPersonalAccount(true);
      
      try {
        // Verificar se já existe um perfil pessoal
        const hasPersonalProfile = businessProfiles.some(profile => profile.profileType === 'personal');
        
        if (hasPersonalProfile) {
          setSuccess("Você já possui uma conta pessoal!");
          setHasPersonalAccount(true);
        } else {
          // Usar a função createProfile do contexto do usuário
          await createProfile(
            {
              profileName: `${user.displayName || 'Usuário'} (Pessoal)`,
              email: user.email,
              isActive: true
            }, 
            'personal'
          );
          
          setHasPersonalAccount(true);
          setSuccess("Conta pessoal criada com sucesso! Agora você pode alternar entre a conta empresarial e pessoal clicando em seu nome no cabeçalho.");
          
          // CORREÇÃO: Remover o window.location.reload() - o contexto já atualiza automaticamente
        }
      } catch (err) {
        console.error("Erro ao criar conta pessoal:", err);
        setError(err.message || "Não foi possível criar sua conta pessoal. Por favor, tente novamente.");
      } finally {
        setCreatingPersonalAccount(false);
        // Tempo maior para ler a mensagem
        setTimeout(() => {
          setSuccess("");
          setError("");
        }, 5000);
      }
    };

    return (
      <div className="settings-tab-content">
        <h3>Funcionalidades</h3>
        <div className="features-container">
          {/* Nova opção de conta pessoal - mostrar apenas se tiver plano business_pro */}
          {activeAccountType === 'business' && hasPro && (
            <div className="feature-option personal-account-option">
              <div className="feature-header">
                <h4 className="feature-title">Conta Pessoal</h4>
                {!hasPersonalAccount ? (
                  <button 
                    className="create-personal-account-button"
                    onClick={handleCreatePersonalAccount}
                    disabled={creatingPersonalAccount}
                  >
                    {creatingPersonalAccount ? (
                      <>
                        <span className="button-spinner"></span>
                        <span>Criando...</span>
                      </>
                    ) : (
                      <>
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                          <circle cx="8.5" cy="7" r="4"></circle>
                          <line x1="20" y1="8" x2="20" y2="14"></line>
                          <line x1="23" y1="11" x2="17" y2="11"></line>
                        </svg>
                        <span>Criar Conta Pessoal</span>
                      </>
                    )}
                  </button>
                ) : (
                  <div className="personal-account-badge">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                      <polyline points="22 4 12 14.01 9 11.01"></polyline>
                    </svg>
                    <span>Conta Pessoal Ativa</span>
                  </div>
                )}
              </div>
              <p className="feature-description">
                Crie uma conta pessoal vinculada à sua conta empresarial para gerenciar suas finanças pessoais separadamente. 
                Após criar, você poderá alternar entre visualizar dados da empresa ou pessoais clicando em seu nome no cabeçalho.
              </p>
              {hasPersonalAccount && (
                <div className="feature-tip">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" y1="16" x2="12" y2="12"></line>
                    <line x1="12" y1="8" x2="12.01" y2="8"></line>
                  </svg>
                  <span>Para alternar entre contas, clique no seu nome no cabeçalho e selecione a conta desejada.</span>
                </div>
              )}
            </div>
          )}

          {/* Opções para conta empresarial */}
          {activeAccountType === 'business' && (
            <div className="feature-option">
              <label className="feature-label">
                <input 
                  type="checkbox" 
                  checked={showAportes}
                  onChange={handleShowAportesChange}
                />
                <span className="feature-text">
                  Mostrar aportes e despesas de sócios ao resultado final no Dashboard
                </span>
              </label>
              <p className="feature-description">
                Quando ativado, exibe a opção para incluir aportes e despesas de sócios nos cálculos de resultado final no Dashboard. Isso permite uma visão mais completa do seu fluxo financeiro.
              </p>
            </div>
          )}

          {/* Opção de categorização automática - apenas para conta empresarial */}
          {activeAccountType === 'business' && (
            <div className="feature-option">
              <label className="feature-label">
                <input 
                  type="checkbox" 
                  checked={automaticBankCategorization}
                  onChange={handleAutomaticBankCategorizationChange}
                />
                <span className="feature-text">
                  Categorização automática para bancos Sicoob/Stone (arquivos OFX)
                </span>
              </label>
              <p className="feature-description">
                Quando ativado, aplica regras de categorização específicas para receitas de bancos Sicoob e Stone importadas via OFX.
                Quando desativado, todas as receitas são categorizadas como "RECEITA.Receitas" independente da origem.
              </p>
            </div>
          )}

          {/* Deduções da receita - apenas para conta empresarial */}
          {activeAccountType === 'business' && (
            <div className="feature-option">
              <label className="feature-label">
                <input
                  type="checkbox"
                  checked={hideRevenueDeductions}
                  onChange={handleHideRevenueDeductionsChange}
                />
                <span className="feature-text">
                  Ocultar "Deduções da Receita" na tabela de Evolução dos Resultados no Dashboard
                </span>
              </label>
              <p className="feature-description">
                Quando marcado, a coluna "Deduções da Receita" será omitida da tabela de resumo na seção "Evolução dos Resultados" do Dashboard para simplificar a visualização dos demais dados. Por padrão, esta coluna é exibida.
              </p>
            </div>
          )}

          {/* Para conta pessoal, adicionar opções específicas se necessário */}
          {activeAccountType === 'personal' && (
            <div className="feature-option">
              <div className="feature-header">
                <h4 className="feature-title">Conta Pessoal Ativa</h4>
              </div>
              <p className="feature-description">
                Você está usando a versão pessoal da ferramenta. Para voltar à conta empresarial, clique no seu nome 
                no cabeçalho e selecione "Conta Empresarial".
              </p>
              <div className="feature-tip">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="16" x2="12" y2="12"></line>
                  <line x1="12" y1="8" x2="12.01" y2="8"></line>
                </svg>
                <span>As categorias e configurações da conta pessoal são independentes da conta empresarial.</span>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <MainLayout userName={user?.displayName || "Usuário"}>
      <div className="settings-container">
        <div className="settings-header">
          <h1>
            {activeAccountType === 'personal' 
              ? 'Configurações da Conta Pessoal' 
              : 'Configurações'}
          </h1>
        </div>
        
        {error && (
          <div className="error-message">
            <button className="close-message" onClick={() => setError("")}>×</button>
            {error}
          </div>
        )}
        
        {success && (
          <div className="success-message">
            <button className="close-message" onClick={() => setSuccess("")}>×</button>
            {success}
          </div>
        )}
        
        {/* MODIFICADO: Adicionar aviso para colaboradores */}
        {isCollaborator && (
          <div className="collaborator-banner" style={{
            backgroundColor: "#f0f7ff",
            borderRadius: "6px",
            padding: "12px 16px",
            marginBottom: "20px",
            borderLeft: "4px solid #3b82f6",
            fontSize: "14px",
            color: "#1e40af",
            display: "flex",
            alignItems: "center"
          }}>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '24px', height: '24px', marginRight: '12px', flexShrink: 0 }}>
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
              <circle cx="9" cy="7" r="4"></circle>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
              <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
            </svg>
            <div>
              <p style={{ fontWeight: 'bold', marginBottom: '4px' }}>Modo Colaborador Ativo</p>
              <p style={{ margin: 0 }}>Você está visualizando as configurações como colaborador. Algumas opções de edição estão restritas.</p>
            </div>
          </div>
        )}
        
        <div className="settings-tabs">
          <button 
            className={`tab-button ${activeTab === 'account' ? 'active' : ''}`}
            onClick={() => setActiveTab('account')}
          >
            Conta
          </button>
          <button 
            className={`tab-button ${activeTab === 'categories' ? 'active' : ''}`}
            onClick={() => setActiveTab('categories')}
          >
            Categorias Financeiras
          </button>
          {activeAccountType === 'business' && (
            <button 
              className={`tab-button ${activeTab === 'cost-types' ? 'active' : ''}`}
              onClick={() => setActiveTab('cost-types')}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                <circle cx="9" cy="7" r="4"></circle>
                <path d="M22 21v-2a4 4 0 0 0-3-3.87"></path>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
              </svg>
              Custos Fixos/Variáveis
            </button>
          )}
          <button 
            className={`tab-button ${activeTab === 'features' ? 'active' : ''}`}
            onClick={() => setActiveTab('features')}
          >
            Funcionalidades
          </button>
        </div>
        
        <div className="settings-content">
          {loading ? (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>Carregando configurações...</p>
            </div>
          ) : (
            <>
              {activeTab === 'account' && (
                <AccountForm 
                  user={user} 
                  setError={setError} 
                  setSuccess={setSuccess} 
                />
              )}
              {activeTab === 'categories' && <CategoriesTab />}
              {activeTab === 'cost-types' && activeAccountType === 'business' && (
                <CategoryCostTypes 
                  categoriesData={categoriesData}
                  setError={setError}
                  setSuccess={setSuccess}
                  activeAccountType={activeAccountType}
                />
              )}
              {activeTab === 'features' && <FeaturesTab />}
            </>
          )}
        </div>
      </div>
    </MainLayout>
  );
};

export default Settings;